import { useEffect, useRef, useState, useCallback, RefObject } from 'react';

export interface UseIntersectionObserverOptions {
  threshold?: number | number[];
  root?: Element | null;
  rootMargin?: string;
  freezeOnceVisible?: boolean;
}

export interface UseIntersectionObserverReturn<T extends HTMLElement> {
  ref: RefObject<T>;
  isVisible: boolean;
  entry: IntersectionObserverEntry | null;
}

/**
 * Hook that tracks whether an element is visible in the viewport
 * using the Intersection Observer API.
 *
 * @param options - Configuration options for the observer
 * @returns Object containing ref to attach to element, visibility state, and entry
 *
 * @example
 * ```tsx
 * const { ref, isVisible } = useIntersectionObserver({
 *   threshold: 0.1,
 *   rootMargin: '200px'
 * });
 *
 * return <div ref={ref}>{isVisible ? 'Visible!' : 'Hidden'}</div>
 * ```
 */
export function useIntersectionObserver<T extends HTMLElement = HTMLDivElement>(
  options: UseIntersectionObserverOptions = {}
): UseIntersectionObserverReturn<T> {
  const {
    threshold = 0,
    root = null,
    rootMargin = '0px',
    freezeOnceVisible = false,
  } = options;

  const ref = useRef<T>(null);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  const updateEntry = useCallback(([entry]: IntersectionObserverEntry[]) => {
    setEntry(entry);
  }, []);

  useEffect(() => {
    const element = ref.current;
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || frozen || !element) return;

    const observerParams = { threshold, root, rootMargin };
    const observer = new IntersectionObserver(updateEntry, observerParams);

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, root, rootMargin, frozen, updateEntry]);

  return {
    ref,
    isVisible: !!entry?.isIntersecting,
    entry,
  };
}

export default useIntersectionObserver;
